// apps/client/src/characters/nametags.ts — DOM name tags over the world (§8.5, contract change 11, revision 9 item 3).
// A friend-colour pill above every resident's head (50 % while away), and above every friend's house; head and house tags share one
// overlap pass that pushes later tags upward until they clear (DESIGN.md "Chips / Name tags"). A head's box in that pass includes the
// chat bubble or emote badge hanging above its pill, so two nearby speakers' bubbles stack instead of covering each other. Tags follow the curved world and
// hide past the horizon, like the geometry under them. M2-T11 adds villager tags (DESIGN.md "Villagers Wear Cream": a cream pill with
// a wood-600 paw, never a friend colour) over villagers' heads (`villagerTag`) and, through `syncHouses('villagers', …)`, their houses.
import { friendColors, tuning, type FriendColor } from '@whiskerton/content';
import type { HouseState, ObjectId, ResidentId, ResidentPublic, Vec2, WorldObject } from '@whiskerton/shared';
import * as THREE from 'three';
import { worldUniforms } from '../engine/shader/worldPatch';

// at = house footprint centre (tile space). Additive: `r` = the resident who lives there (the tag hides while they stand near it, as their
// head tag already says the same), `lift` = the tag's height above the ground (default tuning.camera.houseTagHeight; lower over a tent).
export interface HouseTag { id: ObjectId; name: string; color: FriendColor | 'villager'; at: Vec2; r?: ResidentId; lift?: number }

/** Tag anchor height above a resident's or villager's feet (world units; residents are tuning.move.residentHeight tall). Every doll's top,
 *  a rabbit's ears included, stays under it (doll.chibi.test.ts). */
export const HEAD_TAG_HEIGHT = 1.85;
const HEAD = HEAD_TAG_HEIGHT;
const GAP = 4;         // px between tags pushed apart, and between a tag and the screen edge or the HUD
const AVOID_EVERY_MS = 250;   // how often the HUD's rectangles are re-measured (they move only on resize and HUD changes)
export type Rect = { l: number; t: number; r: number; b: number };
/** The HUD that world tags keep clear of: every element marked `data-hud` (the clock, the top-right buttons, the side buttons). */
/** What tags keep clear of: the HUD, and the toasts (M1 playtest: "They aren't home." covered the owner's house tag at the door). */
export const hudRects = (): Rect[] => [...document.querySelectorAll<HTMLElement>('[data-hud], .toasts > .toast')].map((el) => {
  const r = el.getBoundingClientRect();
  return { l: r.left, t: r.top, r: r.right, b: r.bottom };
}).filter((r) => r.r > r.l && r.b > r.t);
const POINTER = 'position:absolute;left:50%;bottom:-.24em;width:.56em;height:.56em;background:var(--tag, var(--cream-50));'
  + 'transform:translateX(-50%) rotate(45deg);border-bottom-right-radius:.16em;';

const anchors = new Map<ResidentId, HTMLElement>();
const houseEls = new Map<ObjectId, HTMLElement>();
/** The element above a resident's head that chat bubbles (M0-T19) portal into; null when that resident has no tag. */
export function tagAnchor(r: ResidentId): HTMLElement | null { return anchors.get(r) ?? null; }
/** The pill over a house (revision 9 item 3); null when that house is not tagged. */
export function houseTagElement(id: ObjectId): HTMLElement | null { return houseEls.get(id) ?? null; }

/** Every `house` object whose occupant is a known resident, tagged in that resident's friend colour at the footprint centre. */
export function residentHouseTags(objects: readonly WorldObject[], residents: Readonly<Record<ResidentId, ResidentPublic>>): HouseTag[] {
  const out: HouseTag[] = [];
  for (const o of objects) {
    if (o.kind !== 'house') continue;
    const occ = (o.state as HouseState).occupant;
    if (!occ || occ.kind !== 'resident') continue;
    const r = residents[occ.id];
    if (!r) continue;
    const lift = (o.state as HouseState).level === 0 ? tuning.camera.tentTagHeight : tuning.camera.houseTagHeight;
    out.push({ id: o.id, name: r.name, color: r.color, at: { x: o.x + tuning.town.houseW / 2, y: o.y + tuning.town.houseH / 2 }, r: r.id, lift });
  }
  return out;
}
export function houseTagAnchor(at: Vec2, heightAt: (p: Vec2) => number, lift: number = tuning.camera.houseTagHeight): THREE.Vector3 {
  return new THREE.Vector3(at.x, heightAt(at) + lift, at.y);
}

function paint(pill: HTMLElement, color: FriendColor | 'villager'): void {
  if (color === 'villager') {
    pill.className = 'tag tag-villager';
    pill.style.setProperty('--tag', 'var(--cream-50)');
    pill.style.removeProperty('--tag-text');
    return;
  }
  const spec = friendColors[color];
  pill.className = 'tag';
  pill.style.setProperty('--tag', spec.tag);
  pill.style.setProperty('--tag-text', spec.textOnTag);
  pill.style.setProperty('--tag-ink', spec.textOnTag);
}
function pillEl(name: string, color: FriendColor | 'villager', dot: boolean): HTMLElement {
  const pill = document.createElement('span');
  pill.style.position = 'relative';
  setPill(pill, name, color, dot);
  return pill;
}
const SVG = 'http://www.w3.org/2000/svg';
/** The wood-600 paw a villager pill starts with (the same shape as the dialogue panel's speaker tag). */
function pawIcon(): SVGSVGElement {
  const svg = document.createElementNS(SVG, 'svg');
  svg.setAttribute('class', 'paw');
  svg.setAttribute('viewBox', '0 0 24 24');
  svg.setAttribute('aria-hidden', 'true');
  svg.setAttribute('fill', 'currentColor');
  svg.setAttribute('style', 'width:1em;height:1em;flex:none;color:var(--wood-600);');
  for (const [cx, cy, rx, ry] of [[12, 16.2, 5.2, 4.4], [5.4, 10.6, 2.1, 2.6], [9.4, 6.4, 2.1, 2.7], [14.6, 6.4, 2.1, 2.7], [18.6, 10.6, 2.1, 2.6]]) {
    const e = document.createElementNS(SVG, 'ellipse');
    e.setAttribute('cx', String(cx)); e.setAttribute('cy', String(cy)); e.setAttribute('rx', String(rx)); e.setAttribute('ry', String(ry));
    svg.append(e);
  }
  return svg;
}
function setPill(pill: HTMLElement, name: string, color: FriendColor | 'villager', dot: boolean): void {
  paint(pill, color);
  pill.replaceChildren();
  if (color === 'villager') pill.append(pawIcon());
  if (dot) { const d = document.createElement('i'); d.className = 'tag-dot'; pill.append(d); }
  pill.append(document.createTextNode(name));
  const p = document.createElement('i');
  p.setAttribute('aria-hidden', 'true');
  p.style.cssText = POINTER;
  pill.append(p);
}

/** `size`: the pill's box (w × h, measured once per text) and `below`, how far its pointer's tip hangs under the pill's bottom edge. */
type Placed = { el: HTMLElement; size: { w: number; h: number; below?: number } };
/** The pointer under a pill (setPill appends it last): the pill is `el` itself (a house tag) or its first child (a head's anchor). */
function pointerOf(el: HTMLElement): HTMLElement | null {
  const pill = el.classList.contains('tag') ? el : el.firstElementChild;
  return (pill?.lastElementChild as HTMLElement | null | undefined) ?? null;
}
/** How far the pointer's tip reaches below the pill: a square `bottom: -.24em` under it, turned 45° about its centre (POINTER). */
function pointerBelow(el: HTMLElement, pillH: number): number {
  const ptr = pointerOf(el);
  if (!ptr) return 0;
  const side = ptr.offsetHeight;
  return Math.max(0, ptr.offsetTop + side / 2 + side / Math.SQRT2 - pillH);
}
/** What hangs above a head tag: chat bubbles and emote badges portalled into its anchor (M0-T19). Measured once per element and
 *  content, as `up` px above the pill's top and `w` px wide (centred on the pill). */
const hung = new WeakMap<HTMLElement, { key: string; up: number; w: number }>();
function hanging(anchor: HTMLElement, pill: HTMLElement): { up: number; w: number } {
  let up = 0, w = 0;
  for (const c of anchor.children) {
    if (c === pill) continue;
    const el = c as HTMLElement, key = `${el.dataset.tagKey ?? ''}|${el.textContent ?? ''}`;
    let m = hung.get(el);
    if (!m || m.key !== key) { m = { key, up: Math.max(0, -el.offsetTop), w: el.offsetWidth }; hung.set(el, m); }
    up = Math.max(up, m.up); w = Math.max(w, m.w);
  }
  return { up, w };
}
type Head = Placed & { name: string; color: FriendColor; away: boolean; pill: HTMLElement };
type House = Placed & { name: string; color: FriendColor | 'villager'; anchor: THREE.Vector3; r?: ResidentId };
const v = new THREE.Vector3();

/** Villager head tags (M2-T11): module-level, so the villager playback adds them without a NameTags handle; every NameTags.update
 *  projects them with the residents' heads and houses (same overlap push-up) and adopts their elements into its layer. */
type VillagerHead = Placed & { anchor: THREE.Object3D; at: THREE.Vector3 };
const villagerHeads = new Set<VillagerHead>();
/** Shown only while the anchor and every ancestor are visible and it is in a scene graph (a hidden or detached villager has no tag). */
const inWorld = (o: THREE.Object3D): boolean => {
  if (!o.parent) return false;
  for (let p: THREE.Object3D | null = o; p; p = p.parent) if (!p.visible) return false;
  return true;
};
/** A cream villager pill with the paw (DESIGN.md tag-villager), projected every frame above `anchor` (the villager's feet, unbent). */
export function villagerTag(anchor: THREE.Object3D, name: string): { remove(): void } {
  const el = document.createElement('div');
  el.style.cssText = 'position:absolute;left:0;top:0;display:none;will-change:transform;';
  el.append(pillEl(name, 'villager', false));
  const h: VillagerHead = { el, anchor, at: new THREE.Vector3(), size: { w: 0, h: 0 } };
  villagerHeads.add(h);
  return { remove() { villagerHeads.delete(h); el.remove(); } };
}

export class NameTags {
  private readonly layer: HTMLDivElement;
  private readonly heads = new Map<ResidentId, Head>();
  private readonly groups = new Map<string, Map<ObjectId, House>>();
  private you: ResidentId | null = null;
  private readonly avoid: () => readonly Rect[];
  private avoidCache: { at: number; rects: readonly Rect[] } = { at: -Infinity, rects: [] };
  constructor(parent: HTMLElement, avoid: () => readonly Rect[] = hudRects) {
    this.avoid = avoid;
    this.layer = document.createElement('div');
    this.layer.className = 'tags';
    this.layer.style.cssText = 'position:absolute;inset:0;pointer-events:none;overflow:hidden;';
    parent.prepend(this.layer);                                   // under the HUD and panels (React renders after it)
  }
  /** Adds or refreshes a resident's tag: friend colour, in-town dot, and 50 % opacity while away (presence grace). */
  set(r: ResidentId, name: string, color: FriendColor, away: boolean): void {
    let h = this.heads.get(r);
    if (!h) {
      const el = document.createElement('div');
      el.style.cssText = 'position:absolute;left:0;top:0;display:none;will-change:transform;';
      const pill = pillEl(name, color, !away);
      el.append(pill);
      this.layer.append(el);
      h = { el, pill, name, color, away, size: { w: 0, h: 0 } };
      this.heads.set(r, h);
      anchors.set(r, el);
    } else if (h.name !== name || h.color !== color || h.away !== away) {
      setPill(h.pill, name, color, !away);
      Object.assign(h, { name, color, away, size: { w: 0, h: 0 } });
    }
    h.el.style.opacity = away ? '0.5' : '1';
    h.pill.style.visibility = r === this.you ? 'hidden' : '';
  }
  /** Your own tag keeps its anchor (your chat bubbles sit there) but hides its pill. */
  self(r: ResidentId | null): void {
    this.you = r;
    for (const [id, h] of this.heads) h.pill.style.visibility = id === r ? 'hidden' : '';
  }
  remove(r: ResidentId): void {
    const h = this.heads.get(r);
    if (!h) return;
    h.el.remove();
    this.heads.delete(r);
    if (anchors.get(r) === h.el) anchors.delete(r);
  }
  /** Replaces only `group`'s house tags (creating, recolouring or removing pills); other groups are never touched. */
  syncHouses(group: string, tags: readonly HouseTag[], heightAt: (p: Vec2) => number): void {
    let g = this.groups.get(group);
    if (!g) this.groups.set(group, (g = new Map()));
    const keep = new Set(tags.map((x) => x.id));
    for (const [id, h] of g) if (!keep.has(id)) { h.el.remove(); g.delete(id); if (houseEls.get(id) === h.el) houseEls.delete(id); }
    for (const tag of tags) {
      const anchor = houseTagAnchor(tag.at, heightAt, tag.lift);
      const h = g.get(tag.id);
      if (!h) {
        const el = pillEl(tag.name, tag.color, false);
        Object.assign(el.style, { position: 'absolute', left: '0', top: '0', display: 'none', willChange: 'transform' });
        this.layer.append(el);
        g.set(tag.id, { el, name: tag.name, color: tag.color, anchor, r: tag.r, size: { w: 0, h: 0 } });
        houseEls.set(tag.id, el);
        continue;
      }
      h.anchor = anchor;
      h.r = tag.r;
      if (h.name !== tag.name || h.color !== tag.color) {
        setPill(h.el, tag.name, tag.color, false);
        Object.assign(h, { name: tag.name, color: tag.color, size: { w: 0, h: 0 } });
      }
    }
  }
  houseAnchor(id: ObjectId): THREE.Vector3 | null {
    for (const g of this.groups.values()) { const h = g.get(id); if (h) return h.anchor; }
    return null;
  }
  /** Projects every head (feet position + HEAD) and house anchor, then pushes overlapping tags upward until they clear. */
  update(camera: THREE.Camera, heads: ReadonlyMap<ResidentId, THREE.Vector3>): void {
    const W = this.layer.clientWidth || innerWidth, H = this.layer.clientHeight || innerHeight;
    const cam = worldUniforms.uCamXZ.value, curve = worldUniforms.uCurve.value, horizon = tuning.camera.horizon;
    // This frame's screen spots and boxes (the stored tags stay untouched). A box runs from `h` above the tag's anchor spot to `below`
    // under it: a pill and what hangs above it (a chat bubble or emote), down to its pointer's tip. Your own tag hides its pill, so its
    // box is only what hangs above it, and with nothing there it takes no room (v1 round 4: the empty box pushed every tag off its head).
    const shown: { p: Placed; x: number; y: number; w: number; h: number; below: number; room: boolean; head?: Head; self?: boolean; said?: boolean }[] = [];
    const place = (p: Placed, at: THREE.Vector3 | undefined, lift: number, head?: Head, self?: boolean) => {
      if (!at) { p.el.style.display = 'none'; return; }
      const dx = at.x - cam.x, dz = at.z - cam.y, d2 = dx * dx + dz * dz;
      v.set(at.x, at.y + lift - d2 * curve, at.z).project(camera);  // the world shader bends geometry down by d² · uCurve
      if (d2 > horizon * horizon || !(v.z > -1 && v.z < 1) || Math.abs(v.x) > 1.1 || Math.abs(v.y) > 1.1) { p.el.style.display = 'none'; return; }
      p.el.style.display = '';
      shown.push({ p, x: (v.x * 0.5 + 0.5) * W, y: (-v.y * 0.5 + 0.5) * H, w: 0, h: 0, below: 0, room: true, head, self });
    };
    for (const [r, h] of this.heads) place(h, heads.get(r), HEAD, h, r === this.you);
    for (const h of villagerHeads) {
      if (h.el.parentElement !== this.layer) this.layer.append(h.el);
      place(h, inWorld(h.anchor) ? h.anchor.getWorldPosition(h.at) : undefined, HEAD);
    }
    const near = tuning.camera.houseTagHideNear;
    for (const g of this.groups.values()) for (const h of g.values()) {
      const home = h.r ? heads.get(h.r) : undefined;                // its resident at home: their head tag already names them
      place(h, home && Math.hypot(home.x - h.anchor.x, home.z - h.anchor.z) < near ? undefined : h.anchor, 0);
    }
    for (const s of shown) {
      const p = s.p;
      if (p.size.w === 0) { const w = p.el.offsetWidth, h = p.el.offsetHeight; p.size = { w, h, below: pointerBelow(p.el, h) }; }   // once per text
      const extra = s.head && s.head.el.childElementCount > 1 ? hanging(s.head.el, s.head.pill) : { up: 0, w: 0 };
      if (s.self) { s.w = extra.w; s.h = p.size.h + extra.up; s.below = -p.size.h; s.room = extra.up > 0; }   // only what hangs above the hidden pill
      else { s.w = Math.max(p.size.w, extra.w); s.h = p.size.h + extra.up; s.below = p.size.below ?? 0; }
      s.said = extra.up > 0;                                        // a chat bubble or emote hangs above the pill
    }
    shown.sort((a, b) => b.y - a.y);                              // nearest the bottom of the screen keeps its spot
    const now = performance.now();
    if (now - this.avoidCache.at >= AVOID_EVERY_MS) this.avoidCache = { at: now, rects: this.avoid() };
    const hud = [...this.avoidCache.rects].sort((a, b) => a.t - b.t);
    const done: Rect[] = [];
    for (const { p, x, y, w, h, below, room, said } of shown) {
      const l = x - w / 2, r = x + w / 2;
      let b = y, down = false;
      if (room) {                                                 // your own tag with nothing above it takes no room
        const over = (q: Rect) => l < q.r && r > q.l && b - h < q.b && b + below > q.t;
        // Up past every tag already placed, pointer tips included. Never off the top edge, and never under the HUD: a tag that lands on
        // the clock or a button moves just below it (§8.4), and from then on down past the tags there (M2 playtest: it used to land on
        // them). Each way only ever moves one way past each box, so it ends.
        for (let pass = 0; pass <= 2 * (done.length + hud.length) + 2; pass++) {
          if (b - h < GAP) { b = h + GAP; down = true; }
          const bar = hud.find(over);
          if (bar) { b = bar.b + GAP + h; down = true; continue; }
          const hit = done.find(over);
          if (!hit) break;
          b = down ? hit.b + GAP + h : hit.t - GAP - below;
        }
        // A name alone pushed far below its owner would sit on someone else's head and read as theirs (v1 replay: Ravioli's tag on
        // yours), so it hides and takes no room. What someone says or emotes always shows.
        if (!said && b - y > tuning.camera.tagMaxDropPx) { p.el.style.display = 'none'; continue; }
        done.push({ l, r, t: b - h, b: b + below });
      }
      p.el.style.transform = `translate3d(${x.toFixed(1)}px, ${b.toFixed(1)}px, 0) translate(-50%, -100%)`;
    }
  }
  dispose(): void {
    for (const r of [...this.heads.keys()]) this.remove(r);
    for (const g of this.groups.values()) for (const [id, h] of g) if (houseEls.get(id) === h.el) houseEls.delete(id);
    this.groups.clear();
    this.layer.remove();
  }
}
